(() => {
  const EXPECTED_PRODUCT_ID = '9922672001367';
  const STORAGE_KEY = 'sp_blog_attribution_v1';
  const PROPERTY_NAME = 'properties[_sp_attribution]';
  const PROMOTION_ID = 'blog_featured_product_v3';
  const PROMOTION_NAME = 'SidePillow uitgelicht product';
  const PROMO_VERSION = 'hero_featured_product_v3';
  const CARD_POSITION = 'after_dek';
  const ATTRIBUTION_TTL_MS = 7 * 24 * 60 * 60 * 1000;
  const MAX_CLOCK_SKEW_MS = 5 * 60 * 1000;

  const cleanSlug = (value) => {
    const slug = String(value || '').trim().slice(0, 90);
    return /^[a-z0-9-]+$/.test(slug) ? slug : '';
  };

  const normalizeId = (value) => {
    const match = String(value || '').match(/(\d+)(?:\D*)$/);
    return match ? match[1] : '';
  };

  const analyticsProcessingAllowed = () => {
    try {
      return window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true;
    } catch (_error) {
      return false;
    }
  };

  const customerPrivacyApiReady = () =>
    typeof window.Shopify?.customerPrivacy?.analyticsProcessingAllowed === 'function';

  const clearStoredContext = () => {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch (_error) {
      // Attributie mag de productpagina nooit blokkeren.
    }
    try {
      // Ruim ook de oude 30-minutenversie uit sessionStorage op.
      window.sessionStorage.removeItem(STORAGE_KEY);
    } catch (_error) {
      // Attributie mag de productpagina nooit blokkeren.
    }
  };

  const readContext = () => {
    try {
      if (!customerPrivacyApiReady()) return null;
      if (!analyticsProcessingAllowed()) {
        clearStoredContext();
        return null;
      }

      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const context = JSON.parse(raw);
      const selectedAt = Number(context?.selected_at);
      const expiresAt = Number(context?.expires_at);
      if (
        context?.v !== 1 ||
        context.promotion_id !== PROMOTION_ID ||
        context.promotion_name !== PROMOTION_NAME ||
        !cleanSlug(context.blog_slug) ||
        !Number.isFinite(selectedAt) ||
        selectedAt <= 0 ||
        selectedAt > Date.now() + MAX_CLOCK_SKEW_MS ||
        !Number.isFinite(expiresAt) ||
        expiresAt !== selectedAt + ATTRIBUTION_TTL_MS ||
        expiresAt <= Date.now()
      ) {
        window.localStorage.removeItem(STORAGE_KEY);
        return null;
      }
      return context;
    } catch (_error) {
      return null;
    }
  };

  const writeContextFromUrl = () => {
    try {
      if (!customerPrivacyApiReady() || !analyticsProcessingAllowed()) return null;
      const url = new URL(window.location.href);
      if (url.searchParams.get('ref') !== 'blog_product_bridge') return readContext();
      const blogSlug = cleanSlug(url.searchParams.get('ref_blog'));
      if (!blogSlug) return readContext();

      const selectedAt = Date.now();
      const context = {
        v: 1,
        attribution_name: PROMOTION_NAME,
        promotion_id: PROMOTION_ID,
        promotion_name: PROMOTION_NAME,
        promo_version: PROMO_VERSION,
        card_position: CARD_POSITION,
        blog_slug: blogSlug,
        product_id: EXPECTED_PRODUCT_ID,
        selected_at: selectedAt,
        expires_at: selectedAt + ATTRIBUTION_TTL_MS,
      };
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(context));
      return context;
    } catch (_error) {
      return readContext();
    }
  };

  const stripAttributionParameters = () => {
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.get('ref') !== 'blog_product_bridge') return;
      url.searchParams.delete('ref');
      url.searchParams.delete('ref_blog');
      url.searchParams.delete('ref_at');
      const cleanUrl = url.pathname + (url.search ? url.search : '') + url.hash;
      window.history.replaceState(window.history.state, '', cleanUrl);
    } catch (_error) {
      // Attributie mag de productnavigatie nooit blokkeren.
    }
  };

  let context = null;
  if (customerPrivacyApiReady()) {
    context = writeContextFromUrl();
    if (!analyticsProcessingAllowed()) clearStoredContext();
    stripAttributionParameters();
  }

  const propertyValue = () => {
    if (!context) return '';
    return [
      'v1',
      context.promotion_id,
      context.blog_slug,
      Math.floor(Number(context.selected_at) / 1000),
    ].join('|');
  };

  const productForms = (root = document) => {
    const forms = [];
    if (root?.matches?.('form[is="product-form"]')) forms.push(root);
    root?.querySelectorAll?.('form[is="product-form"]').forEach((form) => forms.push(form));
    return forms;
  };

  const removeAttribution = (root = document) => {
    productForms(root).forEach((form) => {
      form
        .querySelectorAll(`input[name="${PROPERTY_NAME}"], input[data-sp-blog-attribution="true"]`)
        .forEach((input) => input.remove());
    });
  };

  const injectAttribution = (root = document) => {
    context = readContext();
    if (!context) {
      removeAttribution(root);
      return;
    }

    productForms(root).forEach((form) => {
      const idInput = form.querySelector('input[name="id"], select[name="id"]');
      if (!idInput) return;

      let input = form.querySelector(`input[name="${PROPERTY_NAME}"]`);
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = PROPERTY_NAME;
        input.dataset.spBlogAttribution = 'true';
        form.appendChild(input);
      }
      input.value = propertyValue();
      input.setAttribute('value', input.value);
    });
  };

  const publish = async (eventName, payload, retries = 0) => {
    if (!analyticsProcessingAllowed()) return false;
    const shopifyAnalytics = window.Shopify?.analytics;
    if (!shopifyAnalytics || typeof shopifyAnalytics.publish !== 'function') {
      if (retries < 20) {
        window.setTimeout(() => publish(eventName, payload, retries + 1), 250);
      }
      return false;
    }
    try {
      const accepted = await Promise.resolve(shopifyAnalytics.publish(eventName, payload));
      return accepted !== false;
    } catch (_error) {
      return false;
    }
  };

  const attributionPayload = () => {
    context = readContext();
    return {
      blog_attributed: Boolean(context),
      attribution_name: context?.attribution_name || 'Geen blogkaartattributie',
      promotion_id: context?.promotion_id || '',
      promotion_name: context?.promotion_name || '',
      promo_version: context?.promo_version || '',
      card_position: context?.card_position || '',
      blog_slug: context?.blog_slug || '',
    };
  };

  const variantIdFromForm = (form) =>
    normalizeId(form?.querySelector('input[name="id"], select[name="id"]')?.value);

  const locationForForm = (form) => {
    if (form?.classList.contains('sticky-atc-bar__form') || form?.closest('.sticky-atc-bar')) {
      return 'sticky_pdp';
    }
    return 'main_pdp';
  };

  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest('form[is="product-form"] [name="add"], .shopify-payment-button__button');
      if (!button) return;
      const form = button.closest('form');
      injectAttribution(form);
      void publish('sidepillow:pdp_cta_click', {
        schema_version: 1,
        product_id: EXPECTED_PRODUCT_ID,
        variant_id: variantIdFromForm(form),
        cta_location: locationForForm(form),
        cta_type: button.closest('.shopify-payment-button') ? 'accelerated_checkout' : 'add_to_cart',
        ...attributionPayload(),
      });
    },
    { capture: true }
  );

  document.addEventListener('variant:changed', (event) => {
    const variant = event.detail?.variant;
    const productId = normalizeId(variant?.product_id || variant?.product?.id || EXPECTED_PRODUCT_ID);
    if (productId && productId !== EXPECTED_PRODUCT_ID) return;

    injectAttribution();
    void publish('sidepillow:pdp_variant_selected', {
      schema_version: 1,
      product_id: EXPECTED_PRODUCT_ID,
      variant_id: normalizeId(variant?.id),
      variant_title: String(variant?.title || '').replace(/\s+/g, ' ').trim().slice(0, 80),
      ...attributionPayload(),
    });
  });

  const observeBuybox = () => {
    if (!customerPrivacyApiReady() || !analyticsProcessingAllowed()) return;
    const buybox = document.querySelector('.main-product-form');
    if (!buybox || !('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.5)) return;
        observer.disconnect();
        void publish('sidepillow:pdp_buybox_view', {
          schema_version: 1,
          product_id: EXPECTED_PRODUCT_ID,
          variant_id: variantIdFromForm(buybox),
          cta_location: 'main_pdp',
          ...attributionPayload(),
        });
      },
      { threshold: [0.5] }
    );
    observer.observe(buybox);
  };

  injectAttribution();
  observeBuybox();

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!form?.matches?.('form[is="product-form"]')) return;
      // Herlees consent en TTL direct voor Shopify de productproperty verstuurt.
      injectAttribution(form);
    },
    { capture: true }
  );

  const mutationObserver = new MutationObserver((records) => {
    records.forEach((record) => {
      record.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) injectAttribution(node);
      });
    });
  });
  mutationObserver.observe(document.documentElement, { childList: true, subtree: true });

  document.addEventListener('shopify:section:load', () => {
    injectAttribution();
    observeBuybox();
  });

  const syncAnalyticsConsent = () => {
    if (!customerPrivacyApiReady()) return;
    if (!analyticsProcessingAllowed()) {
      context = null;
      clearStoredContext();
      removeAttribution();
      stripAttributionParameters();
      return;
    }

    context = writeContextFromUrl();
    stripAttributionParameters();
    injectAttribution();
    observeBuybox();
  };

  document.addEventListener('visitorConsentCollected', syncAnalyticsConsent);
  document.addEventListener('shopifyCustomerPrivacyApiLoaded', syncAnalyticsConsent);
})();
